import { supabase } from "@/integrations/supabase/client";
import { createClient } from '@supabase/supabase-js';
import { Database } from "@/types/database";

// Helper function to create service role client
const createServiceRoleClient = () => {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
  
  if (!SERVICE_ROLE_KEY) {
    throw new Error('Service role key not configured');
  }
  
  return createClient(SUPABASE_URL, SERVICE_ROLE_KEY);
};

export interface RedeemCodeResponse {
  success: boolean;
  message: string;
  code?: {
    plan: string;
    duration_days: number;
  };
}

export interface AdminNotificationPayload {
  title: string;
  body: string;
  targetMode: "all" | "selected";
  targetUserIds?: string[];
  data?: Record<string, unknown>;
}

// Redeem a code and apply the subscription
export const redeemCode = async (userId: string, code: string): Promise<RedeemCodeResponse> => {
  try {
    // Check if the code exists and is active (using regular client is fine for reading)
    const { data: codeData, error: codeError } = await supabase
      .from('redeem_codes')
      .select('*')
      .eq('code', code)
      .eq('is_active', true)
      .is('used_by', null)
      .single();

    if (codeError || !codeData) {
      
      return {
        success: false,
        message: "Geçersiz kod veya bu kod daha önce kullanılmış"
      };
    }

    // Create service role client for admin operations
    const serviceClient = createServiceRoleClient();

    // Calculate new subscription end date
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + codeData.duration_days);

    // Also calculate grace period (3 days after end date)
    const graceUntil = new Date(endDate);
    graceUntil.setDate(graceUntil.getDate() + 3);

    // Update subscription with new plan using service role permissions
    const { error: updateError } = await serviceClient
      .from('subscriptions')
      .upsert({
        user_id: userId,
        plan: codeData.plan,
        ends_at: endDate.toISOString(),
        grace_until: graceUntil.toISOString(),
        source: 'redeem_code',
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (updateError) {
      
      throw updateError;
    }

    // Mark the code as used using service role permissions
    const { error: usedError } = await serviceClient
      .from('redeem_codes')
      .update({
        used_by: userId,
        used_at: new Date().toISOString(),
        is_active: false
      })
      .eq('code', code);

    if (usedError) {
      
      throw usedError;
    }

    // Log activity using service role permissions
    await serviceClient.from('activity_logs').insert({
      user_id: userId,
      action: 'redeem_code',
      metadata: {
        code: code,
        plan: codeData.plan,
        duration_days: codeData.duration_days
      }
    });

    return {
      success: true,
      message: `${codeData.plan} planı başarıyla aktifleştirildi`,
      code: {
        plan: codeData.plan,
        duration_days: codeData.duration_days
      }
    };
  } catch (error) {
    
    return {
      success: false,
      message: "Kod kullanılırken bir hata oluştu"
    };
  }
};

// Save OneSignal user ID for the current user
export const saveOneSignalUserId = async (userId: string): Promise<{ success: boolean }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false };
    }

    const { error } = await supabase
      .from('users')
      .update({ onesignal_user_id: userId })
      .eq('id', user.id);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    
    return { success: false };
  }
};

// Send a push notification (server side implementation would be needed)
export const sendPushNotification = async (
  userIds: string[],
  title: string,
  body: string,
  data: any = {}
): Promise<{ success: boolean; message?: string }> => {
  try {
    // We are now using the OneSignal dashboard for general notifications
    // This function is now restricted to only sending check payment reminders
    
    // Only allow check payment reminders
    if (!data.type || data.type !== 'check_payment_reminder') {
      
      return {
        success: false,
        message: 'Only check payment reminders are allowed'
      };
    }
    
    // Import the OneSignal integration
    const { sendNotification } = await import('@/integrations/onesignal');
    
    // Forward to the OneSignal implementation
    return await sendNotification(userIds, title, body, {
      ...data,
      type: 'check_payment_reminder'
    });
  } catch (error) {
    
    return { 
      success: false,
      message: "Bildirim gönderilemedi"
    };
  }
};

export const sendAdminNotification = async (
  payload: AdminNotificationPayload
): Promise<{ success: boolean; message?: string; insertedCount?: number; pushSent?: boolean }> => {
  try {
    const { data, error } = await supabase.functions.invoke("send-admin-notification", {
      body: payload,
    });

    if (error) {
      return {
        success: false,
        message: error.message || "Bildirim gönderilemedi",
      };
    }

    if (!data?.success) {
      return {
        success: false,
        message: data?.message || "Bildirim gönderilemedi",
      };
    }

    return {
      success: true,
      insertedCount: data.insertedCount,
      pushSent: data.pushSent,
      message: data.pushSent
        ? "Bildirimler gönderildi"
        : "Bildirimler uygulama içi kutuya kaydedildi",
    };
  } catch (error) {
    return {
      success: false,
      message: "Bildirim gönderimi sırasında hata oluştu",
    };
  }
};

// Get storage usage statistics
export const getStorageStats = async (): Promise<{ 
  size: number; 
  limit: number;
  files: number;
  bucketStats: Array<{bucket: string, size: number, files: number}>;
  success: boolean;
}> => {
  try {
    
    
    // First check if we have a stored plan setting
    const { limit: storedLimit } = getStoragePlan();
    
    
    // Supabase project URL from the existing client configuration
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://sjyldlubmtfoujonmtru.supabase.co";
    
    
    // Get service role key from environment variable - this should be set in .env file
    // and should never be exposed to the client
    const SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
    
    if (!SERVICE_ROLE_KEY) {
      
      // Use the RPC fallback if service role key is not available
      return getStorageStatsViaRPC(storedLimit);
    }
    
    // First try to get project settings to determine the actual storage limit based on plan
    // Use stored limit as default
    let storageLimit = storedLimit;
    
    try {
      
      // Fetch project details using Admin API
      const projectResponse = await fetch(`${SUPABASE_URL}/rest/v1/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${SERVICE_ROLE_KEY}`,
          'Content-Type': 'application/json',
          'apikey': SERVICE_ROLE_KEY
        }
      });
      
      
      
      if (projectResponse.ok) {
        const planDetails = await projectResponse.json();
        
        
        // If we have plan information from the API, use it to overwrite the stored plan
        // Otherwise, keep using the stored limit
        if (planDetails.project?.subscription_tier) {
          
          
          // Determine the limit based on the project plan
          if (planDetails.project?.subscription_tier === 'pro') {
            // Pro plan has 100GB storage limit
            storageLimit = 100 * 1024 * 1024 * 1024;
            
            // Update the stored plan
            updateStoragePlan('pro');
          } else if (planDetails.project?.subscription_tier === 'team') {
            // Team plan has 1TB storage limit
            storageLimit = 1024 * 1024 * 1024 * 1024;
            
            // Update the stored plan
            updateStoragePlan('team');
          } else if (planDetails.project?.subscription_tier === 'enterprise') {
            // Enterprise plan - custom limit, using a high default
            storageLimit = 10 * 1024 * 1024 * 1024 * 1024; // 10TB default
            
            // Update the stored plan
            updateStoragePlan('enterprise');
          } else {
            // Free plan
            storageLimit = 1 * 1024 * 1024 * 1024;
            
            // Update the stored plan
            updateStoragePlan('free');
          }
        } else {
          
        }
      } else {
        
        try {
          const errorText = await projectResponse.text();
          
        } catch (e) {
          
        }
      }
    } catch (error) {
      
      // Keep using the stored limit if plan fetch fails
    }
    
    
    // Call Supabase API to get bucket information - use the storage API endpoint
    const response = await fetch(`${SUPABASE_URL}/rest/v1/storage/buckets?select=*`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${SERVICE_ROLE_KEY}`,
        'apikey': SERVICE_ROLE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }
    });
    
    
    
    if (!response.ok) {
      
      try {
        const errorText = await response.text();
        
      } catch (e) {
        
      }
      // Fallback to RPC method if Admin API fails
      return getStorageStatsViaRPC(storageLimit);
    }
    
    const buckets = await response.json();
    
    
    
    // Execute SQL query to get detailed bucket usage
    const { data: usageData, error: usageError } = await supabase.rpc(
      'get_storage_usage_stats',
      {},
      { head: false }
    );
    
    if (usageError) {
      
      throw usageError;
    }
    
    
    
    // Process the storage usage data
    let totalSize = 0;
    let totalFiles = 0;
    const bucketStats = [];
    
    if (usageData && Array.isArray(usageData)) {
      
      for (const bucket of usageData) {
        const bucketSize = parseInt(bucket.total_bytes_used || 0);
        const fileCount = parseInt(bucket.file_count || 0);
        
        bucketStats.push({
          bucket: bucket.bucket_id,
          size: bucketSize,
          files: fileCount
        });
        
        totalSize += bucketSize;
        totalFiles += fileCount;
        
        
      }
    } else {
      
    }
    
    
    
    
    return {
      size: totalSize,
      limit: storageLimit,
      files: totalFiles,
      bucketStats,
      success: true
    };
  } catch (error) {
    
    // Fallback to RPC method if anything fails
    return getStorageStatsViaRPC();
  }
};

// Helper to format bytes for logging
const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  else if (bytes < 1024 * 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  else return (bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2) + ' TB';
};

// Fallback function that uses only RPC (no admin API)
const getStorageStatsViaRPC = async (knownLimit?: number): Promise<{ 
  size: number; 
  limit: number;
  files: number;
  bucketStats: Array<{bucket: string, size: number, files: number}>;
  success: boolean;
}> => {
  try {
    
    
    // Get service role key to use if available
    const SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://sjyldlubmtfoujonmtru.supabase.co";
    
    // Create a client with the service role if it's available
    let client = supabase;
    if (SERVICE_ROLE_KEY) {
      // Import needed to create a new client
      const { createClient } = await import('@supabase/supabase-js');
      client = createClient(SUPABASE_URL, SERVICE_ROLE_KEY);
    }
    
    
    
    const { data, error } = await client.rpc(
      'get_storage_usage_stats',
      {},
      { head: false }
    );
    
    if (error) {
      
      
      // If the RPC function fails, try direct SQL query as a fallback
      
      return getStorageStatsViaDirectSQL(knownLimit);
    }
    
    
    
    // Process the storage usage data
    let totalSize = 0;
    let totalFiles = 0;
    const bucketStats = [];
    
    if (data && Array.isArray(data)) {
      
      for (const bucket of data) {
        const bucketSize = parseInt(bucket.total_bytes_used || 0);
        const fileCount = parseInt(bucket.file_count || 0);
        
        bucketStats.push({
          bucket: bucket.bucket_id,
          size: bucketSize,
          files: fileCount
        });
        
        totalSize += bucketSize;
        totalFiles += fileCount;
        
        
      }
    } else {
      
    }
    
    // Use the known limit if passed, otherwise default to 1GB
    const storageLimit = knownLimit || 1 * 1024 * 1024 * 1024;
    
    
    
    
    
    return {
      size: totalSize,
      limit: storageLimit,
      files: totalFiles,
      bucketStats,
      success: true
    };
  } catch (error) {
    
    
    // Try direct SQL query as a last resort
    
    return getStorageStatsViaDirectSQL(knownLimit);
  }
};

// Fallback that uses direct SQL query to check storage.objects table
const getStorageStatsViaDirectSQL = async (knownLimit?: number): Promise<{ 
  size: number; 
  limit: number;
  files: number;
  bucketStats: Array<{bucket: string, size: number, files: number}>;
  success: boolean;
}> => {
  try {
    
    
    // Get service role key to use if available
    const SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://sjyldlubmtfoujonmtru.supabase.co";
    
    // Create a client with the service role if it's available
    let client = supabase;
    if (SERVICE_ROLE_KEY) {
      // Import needed to create a new client
      const { createClient } = await import('@supabase/supabase-js');
      client = createClient(SUPABASE_URL, SERVICE_ROLE_KEY);
    }
    
    // First check if the storage.objects table exists and we have access to it
    
    
    // Try to query just the structure without accessing data
    // storage.objects is in the storage schema, not public schema
    try {
      // Check if we can access the table directly via RPC
      const { data: tableCheckData, error: tableCheckError } = await client.rpc(
        'exec_sql',
        {
          query_text: `
            SELECT EXISTS (
              SELECT FROM pg_tables 
              WHERE schemaname = 'storage' 
              AND tablename = 'objects'
            );
          `
        }
      );
      
      if (tableCheckError || !tableCheckData || !tableCheckData[0]?.exists) {
        
        throw new Error('Cannot access storage.objects table');
      }
      
      
      
      // If we can access storage.objects, try to run the storage stats query directly
      
      
      // Direct SQL query to get storage usage by bucket
      const { data: sqlData, error: sqlError } = await client.rpc(
        'exec_sql', {
          query_text: `
            SELECT 
              storage.objects.bucket_id, 
              SUM((storage.objects.metadata->>'size')::BIGINT) AS total_bytes_used,
              COUNT(*) AS file_count
            FROM storage.objects
            GROUP BY storage.objects.bucket_id
          `
        });
      
      if (sqlError) {
        
        throw sqlError;
      }
      
      
      
      // Process the SQL query results
      let totalSize = 0;
      let totalFiles = 0;
      const bucketStats = [];
      
      if (sqlData && Array.isArray(sqlData)) {
        for (const row of sqlData) {
          const bucketSize = parseInt(row.total_bytes_used || 0);
          const fileCount = parseInt(row.file_count || 0);
          
          bucketStats.push({
            bucket: row.bucket_id,
            size: bucketSize,
            files: fileCount
          });
          
          totalSize += bucketSize;
          totalFiles += fileCount;
        }
      }
      
      // Use the known limit if passed, otherwise default to 1GB
      const storageLimit = knownLimit || 1 * 1024 * 1024 * 1024;
      
      return {
        size: totalSize,
        limit: storageLimit,
        files: totalFiles,
        bucketStats,
        success: true
      };
      
    } catch (error) {
      
      
      
      // If we can't access storage.objects, try to get buckets from public API
      // This doesn't require admin privileges
      try {
        const { data: publicBuckets } = await client.storage.listBuckets();
        
        
        // Create empty stats for these buckets as we can't get the actual data
        const bucketStats = (publicBuckets || []).map(bucket => ({
          bucket: bucket.name,
          size: 0, // Unknown
          files: 0  // Unknown
        }));
        
        // Use the known limit if passed, otherwise default to 1GB
        const storageLimit = knownLimit || 1 * 1024 * 1024 * 1024;
        
        return {
          size: 0, // We don't know the actual size
          limit: storageLimit,
          files: 0, // We don't know the actual count
          bucketStats,
          success: bucketStats.length > 0 // Success if we got at least the bucket names
        };
      } catch (bucketListError) {
        
        
        // If we can't even list buckets, return some hard-coded defaults for common buckets
        const defaultBuckets = [
          { bucket: 'project-files', size: 0, files: 0 },
          { bucket: 'site-photos', size: 0, files: 0 },
          { bucket: 'profile-avatars', size: 0, files: 0 }
        ];
        
        const storageLimit = knownLimit || 1 * 1024 * 1024 * 1024;
        
        return {
          size: 0,
          limit: storageLimit,
          files: 0,
          bucketStats: defaultBuckets,
          success: false
        };
      }
    }
  } catch (error) {
    
    
    // Return empty stats with the provided limit
    const storageLimit = knownLimit || 1 * 1024 * 1024 * 1024;
    
    return {
      size: 0,
      limit: storageLimit,
      files: 0,
      bucketStats: [],
      success: false
    };
  }
};

// Add a function to set admin status for a user
export const setAdminStatus = async (userId: string, isAdmin: boolean): Promise<{ success: boolean; message?: string }> => {
  try {
    // Create service role client for admin operations
    const serviceClient = createServiceRoleClient();
    
    // First update the public.users table using service role permissions
    const { error: publicUserError } = await serviceClient
      .from('users')
      .update({ is_dev_admin: isAdmin })
      .eq('id', userId);

    if (publicUserError) {
      throw publicUserError;
    }

    // Log the action using service role permissions
    await serviceClient.from('activity_logs').insert({
      user_id: userId,
      action: isAdmin ? 'set_admin_status_true' : 'set_admin_status_false',
      metadata: {
        timestamp: new Date().toISOString()
      }
    });

    return {
      success: true,
      message: isAdmin ? "Admin yetkisi verildi" : "Admin yetkisi kaldırıldı"
    };
  } catch (error) {
    
    return {
      success: false,
      message: "Admin durumu değiştirilirken bir hata oluştu"
    };
  }
};

// Update storage plan settings
export const updateStoragePlan = async (planType: 'free' | 'pro' | 'team' | 'enterprise', customLimit?: number): Promise<{ 
  success: boolean; 
  message: string;
}> => {
  try {
    // In a real implementation, this would update a setting in the database
    // For now, we'll store this in localStorage as a demonstration
    const planLimits = {
      free: 1 * 1024 * 1024 * 1024, // 1GB
      pro: 100 * 1024 * 1024 * 1024, // 100GB
      team: 1024 * 1024 * 1024 * 1024, // 1TB
      enterprise: customLimit || 10 * 1024 * 1024 * 1024 * 1024 // 10TB or custom limit
    };
    
    // Store the plan settings
    localStorage.setItem('storage_plan_type', planType);
    localStorage.setItem('storage_plan_limit', planLimits[planType].toString());
    
    if (customLimit && planType === 'enterprise') {
      localStorage.setItem('storage_plan_custom_limit', customLimit.toString());
    }
    
    return {
      success: true,
      message: `${planType.toUpperCase()} plan settings updated successfully`
    };
  } catch (error) {
    
    return {
      success: false,
      message: "Failed to update storage plan settings"
    };
  }
};

// Get current storage plan
export const getStoragePlan = (): { 
  planType: 'free' | 'pro' | 'team' | 'enterprise';
  limit: number;
} => {
  try {
    const planType = (localStorage.getItem('storage_plan_type') || 'free') as 'free' | 'pro' | 'team' | 'enterprise';
    let limit: number;
    
    if (planType === 'enterprise' && localStorage.getItem('storage_plan_custom_limit')) {
      limit = parseInt(localStorage.getItem('storage_plan_custom_limit') || '0');
    } else {
      limit = parseInt(localStorage.getItem('storage_plan_limit') || '0');
    }
    
    // Apply defaults if needed
    if (!limit) {
      const planLimits = {
        free: 1 * 1024 * 1024 * 1024, // 1GB
        pro: 100 * 1024 * 1024 * 1024, // 100GB
        team: 1024 * 1024 * 1024 * 1024, // 1TB
        enterprise: 10 * 1024 * 1024 * 1024 * 1024 // 10TB default
      };
      limit = planLimits[planType];
    }
    
    return { planType, limit };
  } catch (error) {
    
    // Default to free plan
    return { 
      planType: 'free', 
      limit: 1 * 1024 * 1024 * 1024 // 1GB
    };
  }
};

export const adminAPI = {
  redeemCode,
  saveOneSignalUserId,
  sendPushNotification,
  sendAdminNotification,
  getStorageStats,
  setAdminStatus,
  updateStoragePlan,
  getStoragePlan
}; 